import React, { useState } from 'react';
import { useAuth } from '../../../../shared/contexts/AuthContext';
import PasswordIcon from '../icons/PasswordIcon';
import PasswordStrengthIndicator from './components/PasswordStrengthIndicator';
import PasswordValidationRules from './components/PasswordValidationRules';

interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  passwordsMatch: boolean;
  notSameAsCurrent: boolean;
}

const ChangePasswordDetails: React.FC = () => {
  const { changePassword } = useAuth();
  const [formData, setFormData] = useState<ChangePasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{
    type: 'success' | 'error' | null;
    text: string;
  }>({ type: null, text: '' });

  const handleInputChange = (field: keyof ChangePasswordFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear submit message when user starts typing
    if (submitMessage.type) {
      setSubmitMessage({ type: null, text: '' });
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePassword = (): PasswordValidation => {
    return {
      minLength: formData.newPassword.length >= 8,
      hasUppercase: /[A-Z]/.test(formData.newPassword),
      hasLowercase: /[a-z]/.test(formData.newPassword),
      hasNumber: /[0-9]/.test(formData.newPassword),
      hasSpecialChar: /[^A-Za-z0-9]/.test(formData.newPassword),
      passwordsMatch: formData.newPassword === formData.confirmPassword && formData.confirmPassword !== '',
      notSameAsCurrent: formData.newPassword !== formData.currentPassword && formData.newPassword !== ''
    };
  };

  const isFormValid = (): boolean => {
    const validation = validatePassword();
    return Object.values(validation).every(Boolean) && formData.currentPassword !== '';
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      setSubmitMessage({
        type: 'error',
        text: 'Please ensure all password requirements are met.'
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate API call - replace with actual implementation
      const success = await changePassword(formData.currentPassword, formData.newPassword);
      
      if (success) {
        setSubmitMessage({
          type: 'success',
          text: 'Password changed successfully!'
        });
        
        // Reset form
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setSubmitMessage({
          type: 'error',
          text: 'Current password is incorrect. Please try again.'
        });
      }
    } catch (error) {
      console.error('Password change error:', error);
      setSubmitMessage({
        type: 'error',
        text: 'An error occurred while changing your password. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setSubmitMessage({ type: null, text: '' });
  };

  return (
    <div className="change-password-details">
      <div className="change-password-section">
        <h2 className="section-title">
          <PasswordIcon className="section-icon" />
          Change Password
        </h2>

        {submitMessage.type && (
          <div className={`submit-message ${submitMessage.type}`}>
            {submitMessage.text}
          </div>
        )}
        
        <div className="current-password-section">
          <h3 className="subsection-title">Current Password</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="currentPassword">Current Password</label>
              <div className="password-input-container">
                <input
                  id="currentPassword"
                  type={showPasswords.current ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter your current password"
                  value={formData.currentPassword}
                  onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => togglePasswordVisibility('current')}
                >
                  {showPasswords.current ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="new-password-section">
          <h3 className="subsection-title">New Password</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="newPassword">New Password</label>
              <div className="password-input-container">
                <input
                  id="newPassword"
                  type={showPasswords.new ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter your new password"
                  value={formData.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => togglePasswordVisibility('new')}
                >
                  {showPasswords.new ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              
              {formData.newPassword && (
                <PasswordStrengthIndicator 
                  password={formData.newPassword}
                  className="password-strength-container"
                />
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="confirmPassword">Confirm New Password</label>
              <div className="password-input-container">
                <input
                  id="confirmPassword"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => togglePasswordVisibility('confirm')}
                >
                  {showPasswords.confirm ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
          </div>

          {formData.newPassword && (
            <PasswordValidationRules
              password={formData.newPassword}
              confirmPassword={formData.confirmPassword}
              currentPassword={formData.currentPassword}
              className="password-validation-container"
            />
          )}
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className={`btn-save btn-enhanced ${!isFormValid() || isSubmitting ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={!isFormValid() || isSubmitting}
          >
            {isSubmitting ? 'Changing Password...' : 'Change Password'}
          </button>
          <button 
            type="button" 
            className="btn-cancel btn-enhanced"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordDetails;